import api from '../api';
import { normalizeUrl } from '../../utils/url';

/**
 * Normalize timeline filters POST payload (empty personalViews selectedOption -> null).
 * @param {ApiManagerTimelineFiltersCreate} data
 * @returns {ApiManagerTimelineFiltersCreate}
 */
const normalizeTimelineFiltersPostData = (data) => ({
  ...data,
  personalViews: data?.personalViews ? {
    ...data.personalViews,
    selectedOption: data.personalViews.selectedOption === '' ? null : data.personalViews.selectedOption,
  } : data.personalViews,
});

/**
 * Get timeline filters
 * @param {string} path - Timeline path
 * @param {string | null} [contexte] - The context from URL query parameters
 * @returns {Promise<ApiManagerTimelineFilter>}
 */
export const getTimelineFilters = async (path, contexte = null) => {
  if (!path) {
    throw new Error('path is required');
  }
  const contexteParam = contexte ? `&contexte=${contexte}` : '';
  const response = await api.get(`/timeline-filters?path=${path}${contexteParam}`);
  return response.data;
};

/**
 * Post timeline filters
 * @param {string} path - Timeline path
 * @param {ApiManagerTimelineFiltersCreate} data - Timeline filters payload
 * @param {string | null} [contexte] - The context from URL query parameters
 * @returns {Promise<ApiManagerTimelineFilter>}
 */
export const postTimelineFilters = async (path, data, contexte = null) => {
  if (!path) {
    throw new Error('path is required');
  }
  const normalizedData = normalizeTimelineFiltersPostData(data);
  const contexteParam = contexte ? `&contexte=${contexte}` : '';
  const response = await api.post(`/timeline-filters?path=${path}${contexteParam}`, normalizedData);
  return response.data;
};

/**
 * Get timeline data
 * @param {string} path - Timeline path
 * @param {string | null} [contexte] - The context from URL query parameters
 * @returns {Promise<ApiManagerTimeline>}
 */
export const getTimeline = async (path, contexte = null) => {
  if (!path) {
    throw new Error('path is required');
  }
  const contexteParam = contexte ? `&contexte=${contexte}` : '';
  const response = await api.get(`/timeline?path=${path}${contexteParam}`);
  return response.data;
};

/**
 * Fast-edit an event after drag or resize.
 * @param {string} fastEditUrl - Pre-composed URL from the event tile
 * @param {ApiManagerFastEditEventCreate} payload
 * @returns {Promise<unknown>}
 */
export const postFastEditEvent = async (fastEditUrl, payload) => {
  const response = await api.post(normalizeUrl(fastEditUrl), payload);
  return response.data;
};

/**
 * Delete a timeline event.
 * @param {string} deleteUrl
 * @returns {Promise<unknown>}
 */
export const deleteTimelineEvent = async (deleteUrl) => {
  const response = await api.post(normalizeUrl(deleteUrl));
  return response.data;
};

/**
 * Duplicate a timeline event.
 * @param {string} duplicateUrl
 * @returns {Promise<unknown>}
 */
export const duplicateTimelineEvent = async (duplicateUrl) => {
  const response = await api.post(normalizeUrl(duplicateUrl));
  return response.data;
};
